/**
 * One param's control, built from its spec (D22, `lib/playout/paramForm.ts`):
 * a string as a text field (with `enum`, a picker), a number or integer the
 * same (a numeric `enum` is a picker too), a boolean as a checkbox, an object
 * as a group of its own fields, and a list as its items with add and remove —
 * as deep as the spec goes. It edits a DRAFT (`Draft`); what the draft means
 * as a value, and whether it is valid, is `parseParams`' to say.
 *
 * The one param form both screens use: the operator screen's adlib form
 * (`playout/AdlibParamForm.tsx`, sent whole) and the rundown editor's piece
 * form (`editor/PieceForm.tsx`, each param committed on its own). `idPrefix`
 * keeps their control ids apart; errors are keyed by the param's path
 * (`person.name`, `lines[2]`), which is also how the controls are named.
 */
import { PlusIcon, XIcon } from 'lucide-react';
import type { ParamSpec } from '@norna/playout-engine/productionConfig.js';
import { type Draft, emptyDraft, labelOf, mustFill } from '../../lib/playout/paramForm.js';
import { Button } from '../ui/button.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

export type ParamControlProps = {
  /** The control ids' prefix (`adlib-param` → `adlib-param-name`). */
  readonly idPrefix: string;
  readonly path: string;
  readonly label: string;
  readonly spec: ParamSpec;
  readonly draft: Draft;
  readonly errors: Readonly<Record<string, string>>;
  readonly onChange: (draft: Draft) => void;
};

function idOf(prefix: string, path: string): string {
  return `${prefix}-${path}`;
}

/** The picker's options for an enum spec, as the text the draft holds; null: no enum (a text field). */
function optionsOf(spec: ParamSpec): readonly string[] | null {
  if (spec.type === 'string') return spec.enum;
  if (spec.type === 'number' || spec.type === 'integer') return spec.enum === undefined ? null : spec.enum.map(String);
  return null;
}

function TextControl({ idPrefix, path, label, spec, draft, errors, onChange }: ParamControlProps) {
  const id = idOf(idPrefix, path);
  const error = errors[path];
  const text = draft.kind === 'text' ? draft.text : '';
  const options = optionsOf(spec);
  return (
    <Field invalid={error !== undefined}>
      <Label htmlFor={id}>
        {label}
        {mustFill(spec) ? ' *' : ''}
      </Label>
      {options === null ? (
        <Input
          id={id}
          value={text}
          inputMode={spec.type === 'string' ? undefined : 'decimal'}
          onChange={(e) => onChange({ kind: 'text', text: e.target.value })}
          aria-invalid={error !== undefined}
          aria-describedby={`${id}-error`}
        />
      ) : (
        <NativeSelect id={id} value={text} onChange={(e) => onChange({ kind: 'text', text: e.target.value })} aria-invalid={error !== undefined} aria-describedby={`${id}-error`}>
          <option value="">–</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
      )}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Field>
  );
}

function ObjectControl({ idPrefix, path, label, spec, draft, errors, onChange }: ParamControlProps) {
  if (spec.type !== 'object') return null;
  const fields = draft.kind === 'object' ? draft.fields : {};
  return (
    <fieldset className="grid gap-3 rounded-md border p-3" data-testid="adlib-param-group" data-path={path}>
      <legend className="px-1 text-sm font-medium">
        {label}
        {mustFill(spec) ? ' *' : ''}
      </legend>
      {Object.entries(spec.properties).map(([key, child]) => (
        <ParamControl
          key={key}
          idPrefix={idPrefix}
          path={`${path}.${key}`}
          label={labelOf(key, child)}
          spec={child}
          draft={fields[key] ?? emptyDraft(child)}
          errors={errors}
          onChange={(next) => onChange({ kind: 'object', fields: { ...fields, [key]: next } })}
        />
      ))}
    </fieldset>
  );
}

function ArrayControl({ idPrefix, path, label, spec, draft, errors, onChange }: ParamControlProps) {
  if (spec.type !== 'array') return null;
  const items = draft.kind === 'array' ? draft.items : [];
  const full = spec.maxItems !== null && items.length >= spec.maxItems;
  const error = errors[path];
  return (
    <fieldset className="grid gap-2 rounded-md border p-3" data-testid="adlib-param-list" data-path={path} aria-describedby={`${idOf(idPrefix, path)}-error`}>
      <legend className="px-1 text-sm font-medium">
        {label}
        {mustFill(spec) ? ' *' : ''}
      </legend>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No items.</p> : null}
      {items.map((item, index) => {
        const itemLabel = `${label} ${String(index + 1)}`;
        return (
          // Items have no identity but their place: removing one re-keys those after it, which re-mounts them with their drafts.
          <div key={`${path}[${String(index)}]`} className="flex items-start gap-1.5" data-testid="adlib-param-item">
            <div className="min-w-0 flex-1">
              <ParamControl
                idPrefix={idPrefix}
                path={`${path}[${String(index)}]`}
                label={itemLabel}
                spec={{ ...spec.items, required: true }}
                draft={item}
                errors={errors}
                onChange={(next) => onChange({ kind: 'array', items: items.map((d, i) => (i === index ? next : d)) })}
              />
            </div>
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${itemLabel}`} onClick={() => onChange({ kind: 'array', items: items.filter((_, i) => i !== index) })}>
              <XIcon />
            </Button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="w-fit" disabled={full} onClick={() => onChange({ kind: 'array', items: [...items, emptyDraft(spec.items)] })}>
        <PlusIcon />
        Add {label.toLowerCase()}
      </Button>
      <FieldError id={`${idOf(idPrefix, path)}-error`}>{error}</FieldError>
    </fieldset>
  );
}

export function ParamControl(props: ParamControlProps) {
  const { idPrefix, path, label, spec, draft, onChange } = props;
  if (spec.type === 'boolean') {
    const id = idOf(idPrefix, path);
    return (
      <label className="flex items-center gap-2 text-sm" htmlFor={id}>
        <input id={id} type="checkbox" checked={draft.kind === 'bool' && draft.value} onChange={(e) => onChange({ kind: 'bool', value: e.target.checked })} />
        {label}
      </label>
    );
  }
  if (spec.type === 'object') return <ObjectControl {...props} />;
  if (spec.type === 'array') return <ArrayControl {...props} />;
  return <TextControl {...props} />;
}
